import type { TFunction } from 'i18next';
import type {
  RequirementStatus,
  TeamRequirementKey,
  WorkEnvironmentDocumentation,
} from '../../api/workEnvironmentTypes';
import { fullDate, joinList } from '../HRDashboard/hmsFormat';
import { decisionText } from '../WorkEnvironmentSurveyDetail/archiveView';
import { LEVEL_COLORS } from '../WorkEnvironmentSurveyDetail/riskView';
import {
  currentStepIndex,
  PROCESS_STEPS,
  ProcessStep,
  responsePercent,
  stepState,
} from '../WorkEnvironmentSurveyDetail/surveyProcessView';

/*
 * The API decides the status of each requirement. What is here writes the report out:
 * what the team did, what shows it, and what remains.
 */

type Report = WorkEnvironmentDocumentation;
type Requirement = Report['requirements'][number];
export type TagColor = 'success' | 'info' | 'danger' | 'warning' | 'neutral';

const K = 'workEnvironmentInspection';

export const STATUS_COLORS: Record<RequirementStatus, TagColor> = {
  ok: 'success',
  inProgress: 'info',
  missing: 'danger',
};

const date = (value: string | null, language: string) => (value ? fullDate(value, language) : '');

const count = (report: Report, level: 'high' | 'moderate') =>
  report.areas.filter((area) => area.assessed === level).length;

/**
 * «…NoRep» for the texts of a team without a safety representative, «…LeaderIsRep» where its team leader is the only
 * one, and cannot follow up their own work
 */
const noRep = (report: Report) => {
  if (!report.usesSafetyReps) return 'NotUsed';
  if (report.team.safetyReps.length > 0) return '';
  return report.team.safetyRepMissing === 'leaderIsSafetyRep' ? 'LeaderIsRep' : 'NoRep';
};

export const participationTitle = (report: Report, t: TFunction) =>
  t(`${K}.parts.participation${report.usesSafetyReps ? '' : 'NotUsed'}`);

/**
 * The texts about the assessment follow who took part in it, not only whether the team has a
 * safety representative. «…Alone» when the team leader assessed without the safety representative.
 */
const tookPart = (report: Report) => {
  const participation = report.assessment?.participation;
  if (!report.usesSafetyReps || !participation || participation === 'SAFETY_REP') return noRep(report);
  if (participation === 'EMPLOYEE_REP') return 'EmployeeRep';
  return report.team.safetyReps.length > 0 ? 'Alone' : noRep(report);
};

export type IntroPart = 'riskIntro' | 'participationIntro' | 'sensitiveIntro' | 'signatureIntro';

/** Who assessed the risk, as opposed to who can see it or sign */
const ABOUT_THE_ASSESSMENT: IntroPart[] = ['riskIntro', 'participationIntro'];

/** The introduction of a part, which names the safety representative only when they took part */
export const partIntro = (report: Report, part: IntroPart, t: TFunction) =>
  t(`${K}.parts.${part}${ABOUT_THE_ASSESSMENT.includes(part) ? tookPart(report) : noRep(report)}`, {
    version: report.versions.riskMatrix?.version ?? '',
  });

const nameOf = (report: Report, key: string) => report.areas.find((area) => area.key === key)?.name ?? key;

/** The newest log entries of the actions, as the report refers to them */
const referredEntries = (report: Report, actions: string[]) =>
  report.activity.filter((entry) => actions.includes(entry.action)).slice(0, 3);

/** «L-11246, L-11331» for the log entries of the actions, newest first */
const logIds = (report: Report, actions: string[]) =>
  referredEntries(report, actions)
    .map((entry) => `L-${entry.id}`)
    .join(', ');

const firstAt = (report: Report, action: string) =>
  report.activity.filter((entry) => entry.action === action).at(-1)?.at ?? null;

const lastAt = (report: Report, action: string) =>
  report.activity.find((entry) => entry.action === action)?.at ?? null;

/** Which part of the report shows it, and which log entries */
const EVIDENCE: Record<TeamRequirementKey, { part: string; actions: string[] }> = {
  surveyed: { part: '4', actions: [] },
  assessed: { part: '4', actions: ['RISK_ASSESSMENT_SAVED'] },
  safetyRep: { part: '3', actions: ['RISK_ASSESSMENT_SAVED'] },
  highRiskPlan: { part: '5', actions: ['PLAN_APPROVED'] },
  moderateDecision: { part: '4', actions: ['AREA_DECIDED', 'PLAN_APPROVED'] },
  employees: { part: '6', actions: ['PROPOSALS_CLOSED', 'HIGHLIGHTS_CLOSED'] },
  routines: { part: '7', actions: [] },
  followUp: { part: '5', actions: ['PLAN_APPROVED', 'EVALUATION_STARTED'] },
};

const evidence = (report: Report, key: TeamRequirementKey, t: TFunction) => {
  const { part, actions } = EVIDENCE[key];
  const ids = logIds(report, actions);
  return ids ? t(`${K}.evidence.withLog`, { part, ids }) : t(`${K}.evidence.part`, { part });
};

const areasAt = (report: Report, level: 'high' | 'moderate') =>
  report.areas.filter((area) => area.assessed === level);

const openAreas = (report: Report, level: 'high' | 'moderate') =>
  areasAt(report, level).filter((area) => !area.planned && (level === 'high' || !area.decision));

const LEVEL_OF = { highRiskPlan: 'high', moderateDecision: 'moderate' } as const;

const isAboutAreas = (key: TeamRequirementKey): key is keyof typeof LEVEL_OF => key in LEVEL_OF;

/**
 * The areas the requirement still waits for, as the API gives them, so the report says what the team leader's
 * overview says. A report locked before the API gave them works them out the same way.
 */
const areasLeft = (report: Report, requirement: Requirement) => {
  if (requirement.areas) return requirement.areas.flatMap((key) => report.areas.filter((area) => area.key === key));
  return isAboutAreas(requirement.key) ? openAreas(report, LEVEL_OF[requirement.key]) : [];
};

/** What the team did for the requirement, in a sentence or two */
const howText = (report: Report, requirement: Requirement, t: TFunction, language: string): string => {
  const { survey, versions, assessment } = report;
  const assessedOn = date(assessment?.savedAt ?? null, language);
  const plan = report.plans[0];
  switch (requirement.key) {
    case 'surveyed':
      return survey.responses >= survey.minResponses
        ? t(`${K}.how.surveyed`, {
            responses: survey.responses,
            expected: survey.expected,
            version: versions.template.version,
            factors: versions.template.factors,
          })
        : t(`${K}.how.tooFew`, { responses: survey.responses, min: survey.minResponses });
    case 'assessed':
      return assessment?.shortLongTerm.trim()
        ? t(`${K}.how.assessed`, { date: assessedOn })
        : t(`${K}.how.notAssessed`);
    case 'safetyRep':
      if (!assessment) return t(`${K}.how.notAssessed`);
      const alone = assessment.participation === 'NONE' ? noRep(report) : '';
      return t(`${K}.how.participation.${assessment.participation}${alone}`, {
        date: assessedOn,
        role: assessment.representativeRole ?? t(`${K}.how.employeeRep`),
      });
    case 'highRiskPlan':
    case 'moderateDecision': {
      const total = count(report, LEVEL_OF[requirement.key]);
      if (total === 0) return t(`${K}.how.${requirement.key}.none`);
      const left = areasLeft(report, requirement);
      if (left.length === 0) return t(`${K}.how.${requirement.key}.done`, { count: total });
      // «2 av 3 områder … har tiltaksplan. Mangler: Vold.», or that none has one yet
      return t(`${K}.how.${requirement.key}.${left.length < total ? 'some' : 'nothing'}`, {
        done: total - left.length,
        count: total,
        areas: joinList(left.map((area) => area.name), t, 'and'),
      });
    }
    case 'employees':
      return requirement.status === 'ok'
        ? t(`${K}.how.employees`, {
            proposals: report.participation.proposals,
            voters: report.participation.voters,
            expected: survey.expected,
            fromTeam: report.measures.filter((measure) => measure.fromProposals).length,
            measures: report.measures.length,
          })
        : t(`${K}.how.employeesOngoing`);
    case 'routines':
      return [
        t(`${K}.how.routines.whistleblowing.${report.routines.whistleblowing}`),
        t(`${K}.how.routines.followUp.${report.routines.followUp}`),
      ].join(' ');
    case 'followUp':
      if (report.evaluation) return t(`${K}.how.evaluated`, { date: date(report.evaluation.evaluatedAt, language) });
      if (!plan) return t(`${K}.how.${requirement.status === 'ok' ? 'noPlanNeeded' : 'noPlan'}`);
      return plan.frequency && plan.evaluationDate
        ? t(`${K}.how.followUp`, {
            frequency: t(`workEnvironmentFollowUp.every.${plan.frequency}`),
            date: date(plan.evaluationDate, language),
          })
        : t(`${K}.how.followUpMissing`);
  }
};

/** What remains, when the requirement is not met */
const gapText = (report: Report, requirement: Requirement, t: TFunction, language: string) => {
  if (requirement.status === 'ok') return '';
  const due = requirement.due ? date(requirement.due, language) : '';
  if (isAboutAreas(requirement.key)) {
    const names = areasLeft(report, requirement).map((area) => area.name);
    // High risk needs an approved plan, where a decision is enough for moderate risk
    const gap = requirement.key === 'highRiskPlan' ? 'highAreas' : 'areas';
    return t(`${K}.gap.${gap}.${requirement.status}`, { areas: joinList(names, t, 'and'), date: due });
  }
  if (requirement.key === 'safetyRep' && report.assessment?.absenceReason)
    return t(`${K}.gap.absence`, { reason: report.assessment.absenceReason });
  return t(`${K}.gap.${requirement.key}`);
};

export type RequirementCard = {
  key: TeamRequirementKey;
  number: number;
  status: RequirementStatus;
  how: string;
  evidence: string;
  gap: string;
  /** The areas the requirement still waits for, which the web view links to the plan step for */
  areasLeft: Array<{ key: string; name: string }>;
};

export const requirementCards = (report: Report, t: TFunction, language: string): RequirementCard[] =>
  report.requirements.map((requirement, index) => ({
    key: requirement.key,
    number: index + 1,
    status: requirement.status,
    how: howText(report, requirement, t, language),
    evidence: evidence(report, requirement.key, t),
    gap: gapText(report, requirement, t, language),
    areasLeft:
      requirement.status !== 'ok' && isAboutAreas(requirement.key)
        ? areasLeft(report, requirement).map(({ key, name }) => ({ key, name }))
        : [],
  }));

export const onlyGaps = <T extends { status: RequirementStatus }>(items: T[], showOnlyGaps: boolean) =>
  showOnlyGaps ? items.filter((item) => item.status !== 'ok') : items;

export const tally = (report: Report) =>
  (['ok', 'inProgress', 'missing'] as const).map((status) => ({
    status,
    count: report.requirements.filter((requirement) => requirement.status === status).length,
  }));

export const summaryText = (report: Report, t: TFunction) => {
  const [ok, inProgress, missing] = tally(report).map((item) => item.count);
  return [
    t(`${K}.summary.${report.plans.length > 0 ? 'withPlan' : 'withoutPlan'}`, { team: report.team.name }),
    t(`${K}.summary.count`, { count: report.requirements.length, ok, inProgress, missing }),
  ].join(' ');
};

export type RemainingItem = {
  key: string;
  title: string;
  fix: string;
  /** «Ansvarlig: … · Frist … · Krav …» */
  meta: string;
  status: string;
  color: TagColor;
};

const metaLine = (who: string, due: string, reference: string, t: TFunction) =>
  [
    t(`${K}.labels.who`, { who }),
    due ? t(`${K}.labels.due`, { date: due }) : '',
    t(`${K}.labels.reference`, { reference }),
  ]
    .filter(Boolean)
    .join(' · ');

/** Where the fix names the safety representative, the leader does it alone when the organisation has none */
const FIXED_WITH_SAFETY_REP: TeamRequirementKey[] = ['surveyed', 'assessed', 'highRiskPlan'];

const fixSuffix = (report: Report, key: TeamRequirementKey) => {
  if (key === 'safetyRep') return noRep(report);
  return !report.usesSafetyReps && FIXED_WITH_SAFETY_REP.includes(key) ? 'NotUsed' : '';
};

/** Every requirement not met yet, and the evaluation until it is done */
export const remaining = (report: Report, t: TFunction, language: string): RemainingItem[] => {
  const leaders = report.team.leaders.join(', ') || t(`${K}.roles.TEAM_LEADER`);
  const gaps = report.requirements
    .filter((requirement) => requirement.status !== 'ok')
    .map((requirement) => ({
      key: requirement.key,
      title: t(`${K}.remaining.${requirement.key}.title`),
      fix: t(`${K}.remaining.${requirement.key}.fix${fixSuffix(report, requirement.key)}`),
      meta: metaLine(
        requirement.key === 'routines' ? t(`${K}.roles.HR_ADMIN`) : leaders,
        date(requirement.due, language),
        t(`${K}.requirements.${requirement.key}.short`),
        t,
      ),
      status: t(`${K}.status.${requirement.status}`),
      color: STATUS_COLORS[requirement.status],
    }));
  const evaluationDate = report.plans.find((plan) => plan.evaluationDate)?.evaluationDate ?? null;
  const evaluation =
    !report.evaluation && evaluationDate
      ? [
          {
            key: 'evaluation',
            title: t(`${K}.remaining.evaluation.title`),
            fix: t(`${K}.remaining.evaluation.fix`, { date: date(evaluationDate, language) }),
            meta: metaLine(leaders, date(evaluationDate, language), t(`${K}.remaining.evaluation.short`), t),
            status: t(`${K}.status.planned`),
            color: 'info' as const,
          },
        ]
      : [];
  return [...gaps, ...evaluation];
};

export const headerFacts = (report: Report, t: TFunction, language: string) => {
  const { business, team, survey, versions } = report;
  const matrix = versions.riskMatrix;
  return [
    { key: 'business', value: business.name },
    ...(business.orgNumber ? [{ key: 'orgNumber', value: orgNumber(business.orgNumber) }] : []),
    { key: 'team', value: t(`${K}.header.teamValue`, { team: team.name, count: team.size }) },
    { key: 'leader', value: team.leaders.join(', ') || t(`${K}.header.none`) },
    ...(report.usesSafetyReps
      ? [{ key: 'safetyRep', value: team.safetyReps.join(', ') || t(`${K}.header.noSafetyRep`) }]
      : []),
    { key: 'surveyId', value: String(survey.id) },
    {
      key: 'template',
      value: t(`${K}.header.templateValue`, { version: versions.template.version, count: versions.template.factors }),
    },
    ...(matrix
      ? [
          {
            key: 'riskMatrix',
            value: matrix.approvedAt
              ? t(`${K}.header.matrixApproved`, { version: matrix.version, date: date(matrix.approvedAt, language) })
              : t(`${K}.header.matrixValue`, { version: matrix.version }),
          },
        ]
      : []),
    {
      key: 'previous',
      value: survey.previousAt ? date(survey.previousAt, language) : t(`${K}.header.firstSurvey`),
    },
  ].map((fact) => ({ ...fact, label: t(`${K}.header.${fact.key}`) }));
};

/** «912 345 688» */
export const orgNumber = (value: string) => value.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');

export type StepRow = {
  step: ProcessStep;
  number: number;
  date: string;
  text: string;
  log: string;
  state: 'done' | 'current' | 'notStarted';
};

const STEP_ACTIONS: Partial<Record<ProcessStep, string>> = {
  risk: 'RISK_ASSESSMENT_SAVED',
  data: 'DATA_SOURCE_ADDED',
  propose: 'PROPOSALS_OPENED',
  highlight: 'HIGHLIGHTS_CLOSED',
  plan: 'PLAN_APPROVED',
  evaluation: 'EVALUATION_STARTED',
};

const STEP_WITHOUT_DATE = { done: 'done', current: 'ongoing', notStarted: 'notStarted' } as const;

const stepDate = (report: Report, step: ProcessStep): string | null => {
  const planned = report.plans.find((plan) => plan.evaluationDate)?.evaluationDate ?? null;
  const dates: Record<ProcessStep, string | null> = {
    draft: report.survey.createdAt,
    sent: report.survey.createdAt,
    risk: report.survey.riskCalculatedAt,
    data: firstAt(report, 'DATA_SOURCE_ADDED'),
    propose: firstAt(report, 'PROPOSALS_OPENED'),
    highlight: lastAt(report, 'HIGHLIGHTS_CLOSED'),
    plan: lastAt(report, 'PLAN_APPROVED'),
    followUp: report.plans[0]?.approvedAt ?? null,
    evaluation: report.evaluation?.evaluatedAt ?? planned,
  };
  return dates[step];
};

const stepText = (report: Report, step: ProcessStep, t: TFunction, language: string) => {
  const { survey, assessment, participation, versions } = report;
  const plan = report.plans[0];
  const texts: Record<ProcessStep, string> = {
    draft: t(`${K}.steps.draft`, {
      template: versions.template.version,
      matrix: versions.riskMatrix?.version ?? '',
    }),
    sent: survey.responseDeadline
      ? t(`${K}.steps.sent`, { count: survey.expected, date: date(survey.responseDeadline, language) })
      : t(`${K}.steps.sentNoDeadline`, { count: survey.expected }),
    risk: [
      t(`${K}.steps.risk`, { responses: survey.responses, expected: survey.expected }),
      assessment ? t(`${K}.steps.reviewed.${assessment.participation}`) : '',
      assessment?.adjustments ? t(`${K}.steps.adjusted`, { count: assessment.adjustments }) : '',
    ]
      .filter(Boolean)
      .join(' '),
    data: t(`${K}.steps.data`, { count: participation.dataSources }),
    propose: t(`${K}.steps.propose`, { count: participation.proposals }),
    highlight: t(`${K}.steps.highlight`, { voters: participation.voters, expected: survey.expected }),
    plan: t(`${K}.steps.plan`, { count: report.plans.length }),
    followUp: plan?.frequency
      ? [
          t(`${K}.steps.followUp`, { frequency: t(`workEnvironmentFollowUp.every.${plan.frequency}`) }),
          plan.nextReview ? t(`${K}.steps.nextReview`, { date: date(plan.nextReview, language) }) : '',
        ]
          .filter(Boolean)
          .join(' ')
      : t(`${K}.steps.noFollowUp`),
    evaluation: report.evaluation
      ? t(`${K}.steps.evaluated`, { responses: report.evaluation.responses })
      : t(`${K}.steps.evaluation`),
  };
  return texts[step];
};

/** The nine steps of the survey, with the day each was done and the log entry that shows it */
export const stepRows = (report: Report, t: TFunction, language: string): StepRow[] => {
  const current = currentStepIndex(report.survey.status);
  return PROCESS_STEPS.map((step, index) => {
    const state = stepState(index, current);
    const action = STEP_ACTIONS[step];
    const at = stepDate(report, step);
    return {
      step,
      number: index + 1,
      date: at ? date(at, language) : t(`${K}.steps.${STEP_WITHOUT_DATE[state]}`),
      text: stepText(report, step, t, language),
      log: action ? logIds(report, [action]) : '',
      state,
    };
  });
};

export const figures = (items: Array<[string, string | number, string?]>, t: TFunction, prefix: string) =>
  items.map(([key, value, sub]) => ({ key, label: t(`${K}.${prefix}.${key}`), value: String(value), sub: sub ?? '' }));

export const methodFigures = (report: Report, t: TFunction) => {
  const { responses, expected, minResponses } = report.survey;
  return figures(
    [
      [
        'responses',
        t(`${K}.ofTotal`, { count: responses, total: expected }),
        t(`${K}.method.rate`, { percent: responsePercent(responses, expected) }),
      ],
      [
        'minimum',
        t(`${K}.method.minimumValue`, { count: minResponses }),
        t(`${K}.method.${responses >= minResponses ? 'reached' : 'notReached'}`),
      ],
      ['high', t(`${K}.areasCount`, { count: count(report, 'high') }), t(`${K}.method.afterAssessment`)],
      ['moderate', t(`${K}.areasCount`, { count: count(report, 'moderate') })],
    ],
    t,
    'method',
  );
};

export type RiskRow = {
  key: string;
  name: string;
  sensitive: boolean;
  calculated: { label: string; color: TagColor };
  assessed: { label: string; color: TagColor };
  adjustment: string;
  decision: string;
  after: { label: string; color: TagColor } | null;
};

const levelTag = (level: Report['areas'][number]['assessed'], sensitive: boolean, t: TFunction) =>
  sensitive
    ? { label: t(`${K}.risk.shielded`), color: 'neutral' as const }
    : { label: t(`workEnvironmentRisk.matrix.levels.${level}`), color: LEVEL_COLORS[level] };

const decisionOf = (area: Report['areas'][number], t: TFunction) => {
  if (area.planned) return t(`${K}.risk.decisions.plan`);
  if (area.decision) return t(`${K}.risk.decisions.${area.decision}`);
  return t(`${K}.risk.decisions.${area.assessed === 'low' ? 'none' : 'pending'}`);
};

/**
 * Every area with the calculated and the assessed level, and what was decided. The team is
 * small, so the sensitive areas are shown without a level, as for the team.
 */
export const riskRows = (report: Report, t: TFunction): RiskRow[] =>
  report.areas.map((area) => ({
    key: area.key,
    name: area.name,
    sensitive: area.sensitive,
    calculated: levelTag(area.calculated, area.sensitive, t),
    assessed: levelTag(area.assessed, area.sensitive, t),
    adjustment:
      area.adjustment && !area.sensitive
        ? t(`${K}.risk.adjusted`, {
            from: t(`workEnvironmentRisk.matrix.levels.${area.calculated}`).toLowerCase(),
            to: t(`workEnvironmentRisk.matrix.levels.${area.assessed}`).toLowerCase(),
            reason: area.adjustment,
          })
        : '',
    decision: decisionOf(area, t),
    after: area.after ? levelTag(area.after, area.sensitive, t) : null,
  }));

export const planIntro = (report: Report, t: TFunction) => {
  const covered = [...new Set(report.plans.flatMap((plan) => plan.areas))].map((key) => nameOf(report, key));
  if (covered.length === 0) return t(`${K}.plans.none`);
  const deviations = report.plans.filter((plan) => plan.deviation).length;
  return [
    t(`${K}.plans.intro`, { count: report.plans.length, areas: joinList(covered, t, 'and') }),
    t(`${K}.plans.${approvalOf(report, deviations)}`, { count: deviations }),
    ...report.plans.flatMap((plan) =>
      plan.deviation ? [t(`${K}.plans.reason`, { reason: plan.deviation })] : [],
    ),
  ].join(' ');
};

/** Who approved the plans. Plans approved before the approver was kept make no claim. */
const approvalOf = (report: Report, deviations: number) => {
  if (!report.usesSafetyReps) return 'byTeamLeader';
  if (deviations > 0) return 'withoutSafetyRep';
  return report.plans.every((plan) => plan.bySafetyRep === true) ? 'bySafetyRep' : 'approved';
};

export const measureFigures = (report: Report, t: TFunction) => {
  const { total, done, onTrack, overdue } = report.measureCounts;
  const areas = new Set(report.measures.flatMap((measure) => measure.areas)).size;
  return figures(
    [
      ['total', total, t(`${K}.areasFor`, { count: areas })],
      ['done', done],
      ['onTrack', onTrack],
      ['overdue', overdue],
    ],
    t,
    'measures',
  );
};

export const measureRows = (report: Report, t: TFunction, language: string) =>
  report.measures.map((measure, index) => ({
    key: `${index}-${measure.title}`,
    title: measure.title,
    source: t(`${K}.measures.${measure.fromProposals ? 'fromTeam' : 'own'}`),
    areas: measure.areas.map((key) => nameOf(report, key)).join(', '),
    owner: measure.owner ?? t(`${K}.measures.noOwner`),
    due: date(measure.dueDate, language),
    status: t(`workEnvironmentFollowUp.measures.statuses.${measure.status}`),
    color: (measure.status === 'DONE' ? 'success' : measure.status === 'IN_PROGRESS' ? 'info' : 'neutral') as TagColor,
  }));

export const evaluationText = (report: Report, t: TFunction, language: string) => {
  const { evaluation } = report;
  if (evaluation)
    return [
      t(`${K}.evaluation.done`, { date: date(evaluation.evaluatedAt, language), responses: evaluation.responses }),
      evaluation.nextSurveyBy ? t(`${K}.evaluation.nextSurvey`, { date: date(evaluation.nextSurveyBy, language) }) : '',
    ]
      .filter(Boolean)
      .join(' ');
  const planned = report.plans.find((plan) => plan.evaluationDate)?.evaluationDate ?? null;
  return planned ? t(`${K}.evaluation.planned`, { date: date(planned, language) }) : t(`${K}.evaluation.notPlanned`);
};

export const residualRows = (report: Report, t: TFunction, language: string) =>
  (report.evaluation?.residual ?? []).map((area) => ({
    name: area.name,
    level: { label: t(`workEnvironmentRisk.matrix.levels.${area.level}`), color: LEVEL_COLORS[area.level] },
    decision: area.decision ? decisionText(area.decision, t, language) : t(`${K}.evaluation.undecided`),
  }));

/** The safety representative is only shown where the organisation uses them */
export const participationFigures = (report: Report, t: TFunction, language: string) => {
  const { survey, participation } = report;
  return figures(
    [
      [
        'responses',
        t(`${K}.ofTotal`, { count: survey.responses, total: survey.expected }),
        t(`${K}.participation.anonymous`),
      ],
      ['proposals', participation.proposals, t(`${K}.participation.withoutNames`)],
      ['voters', t(`${K}.ofTotal`, { count: participation.voters, total: survey.expected })],
      ...(report.usesSafetyReps ? [safetyRepFigure(report, t, language)] : []),
    ],
    t,
    'participation',
  );
};

const safetyRepFigure = (report: Report, t: TFunction, language: string): [string, string, string] => {
  const { assessment } = report;
  const approved = report.plans.find((plan) => plan.approvedAt && plan.bySafetyRep === true)?.approvedAt ?? null;
  const rep = assessment?.participation ?? 'NONE';
  return [
    'safetyRep',
    t(`${K}.participation.rep.${rep}`),
    [
      assessment ? t(`${K}.participation.assessedOn`, { date: date(assessment.savedAt, language) }) : '',
      approved ? t(`${K}.participation.approvedOn`, { date: date(approved, language) }) : '',
    ]
      .filter(Boolean)
      .join(', '),
  ];
};

/** The sensitive areas are only shown as assessed, never with a level */
export const sensitiveItems = (report: Report, t: TFunction, language: string) => {
  const assessedOn = date(report.assessment?.savedAt ?? null, language);
  return [
    ...report.areas
      .filter((area) => area.sensitive)
      .map((area) => ({
        key: area.key,
        title: area.name,
        text: report.assessment
          ? t(`${K}.sensitive.assessed${tookPart(report)}`, { date: assessedOn })
          : t(`${K}.sensitive.notAssessed`),
        status: t(`${K}.sensitive.${report.assessment ? 'assessedTag' : 'notAssessedTag'}`),
        color: (report.assessment ? 'success' : 'warning') as TagColor,
      })),
    ...(['whistleblowing', 'followUp'] as const).map((routine) => ({
      key: routine,
      title: t(`${K}.sensitive.${routine}.title`),
      text: t(`${K}.sensitive.${routine}.${report.routines[routine]}`),
      status: t(`${K}.sensitive.${report.routines[routine] ? 'inPlace' : 'missing'}`),
      color: (report.routines[routine] ? 'success' : 'warning') as TagColor,
    })),
  ];
};

export const integrityLines = (report: Report, t: TFunction) =>
  [
    t(`${K}.integrity.anonymous`, { count: report.survey.minResponses }),
    t(`${K}.integrity.locked`, {
      template: report.versions.template.version,
      matrix: report.versions.riskMatrix?.version ?? '',
    }),
    t(`${K}.integrity.adjustments`),
    t(`${K}.integrity.log`),
  ];

/** «30.09.2026 13.58» */
const logTime = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Oslo',
  })
    .format(new Date(value))
    .replace(',', '');

export const LOG_ROWS = 12;

/** Every entry the requirements and the steps refer to, so each can be checked in the log */
const referredIds = (report: Report) =>
  new Set(
    [
      ...report.requirements.map((requirement) => EVIDENCE[requirement.key].actions),
      ...Object.values(STEP_ACTIONS).map((action) => [action]),
    ].flatMap((actions) => referredEntries(report, actions).map((entry) => entry.id)),
  );

/** The latest entries, and every entry the report refers to, newest first */
export const logRows = (report: Report, t: TFunction, language: string) => {
  const referred = referredIds(report);
  return report.activity
    .filter((entry, index) => index < LOG_ROWS || referred.has(entry.id))
    .map((entry) => ({
    id: `L-${entry.id}`,
    at: logTime(entry.at, language),
    role: t(`${K}.roles.${entry.role}`, { defaultValue: entry.role }),
    event: t(`workEnvironmentRisk.activity.events.${entry.action}`, { defaultValue: entry.action }),
  }));
};

const SIGNATURE_COLORS: Record<NonNullable<Report['signature']>['status'], TagColor> = {
  REQUESTED: 'warning',
  SIGNED: 'success',
  RETURNED: 'danger',
  DEVIATION: 'warning',
  WITHDRAWN: 'neutral',
};

/** Only the safety representative who answered is named. A deviation or a withdrawal names the team's. */
const ANSWERED: Array<NonNullable<Report['signature']>['status']> = ['SIGNED', 'RETURNED'];

export const signatures = (report: Report, t: TFunction, language: string) => {
  const { signature } = report;
  const leader = {
    key: 'leader',
    role: t(`${K}.roles.TEAM_LEADER`),
    name: report.team.leaders.join(', ') || t(`${K}.header.none`),
    status: t(`${K}.signature.responsible`),
    color: 'neutral' as TagColor,
    note: '',
  };
  if (!report.usesSafetyReps) return [leader];
  return [
    leader,
    {
      key: 'safetyRep',
      role: t(`${K}.roles.SAFETY_REP`),
      name:
        (signature && ANSWERED.includes(signature.status) && signature.respondedBy) ||
        report.team.safetyReps.join(', ') ||
        t(`${K}.header.noSafetyRep`),
      status: signature
        ? t(`${K}.signature.${signature.status}`, {
            date: date(signature.respondedAt ?? signature.requestedAt, language),
          })
        : t(`${K}.signature.notRequested${noRep(report)}`),
      color: signature ? SIGNATURE_COLORS[signature.status] : ('neutral' as TagColor),
      note: signature?.comment ? t(`${K}.signature.reason`, { reason: signature.comment }) : '',
    },
  ];
};

export const generatedText = (report: Report, t: TFunction, language: string) =>
  t(`${K}.header.generated`, {
    date: logTime(report.generated.at, language),
    by: [report.generated.by, report.generated.role ? t(`${K}.roles.${report.generated.role}`).toLowerCase() : '']
      .filter(Boolean)
      .join(', '),
  });

/** Null while the report shows the current data */
export const lockedText = (report: Report, t: TFunction, language: string) =>
  report.locked && t(`${K}.header.locked.${report.locked.reason}`, { date: logTime(report.locked.at, language) });
